import {
	FieldOption,
	ManualLookupConfiguration,
	MappingEntitySchema,
	MappingGridRow,
	MappingRuleMode,
	TreeNode
} from './models/link-generic-mapper.models';

export interface MapperGuideSection {
	title: string;
	description: string;
	rows: MappingGridRow[];
}

export interface MapperGuideCaseDetail {
	tabs: Array<'Create' | 'Update' | 'Writeback' | 'Messages'>;
	title: string;
	summary: string;
	steps: string[];
	exampleInput: string;
	exampleResult: string;
}

export interface MapperGuideMarkerLegendItem {
	className: string;
	label: string;
	description: string;
}

export interface MapperGuideDemo {
	sourceSystemName: string;
	targetSystemName: string;
	sourceFieldOptions: FieldOption[];
	sourceTreeItems: TreeNode[];
	targetSchema: MappingEntitySchema;
	targetTreeItems: TreeNode[];
	rows: MappingGridRow[];
	sections: MapperGuideSection[];
	caseDetails: Record<string, MapperGuideCaseDetail>;
	markerLegend: MapperGuideMarkerLegendItem[];
	referenceFieldOptionsByEntity: Record<string, FieldOption[]>;
}

export function createMapperGuideDemo(): MapperGuideDemo {
	const sourceSystemName = 'Demo source';
	const targetSystemName = 'Demo target';
	const sourceFieldOptions: FieldOption[] = [
		createMapperGuideSourceField('companyName', 'Company name', 'String'),
		createMapperGuideSourceField('companyNumber', 'Company number', 'String', true),
		createMapperGuideSourceField('companyType', 'Company type', 'Number'),
		createMapperGuideSourceField('classification', 'Classification', 'String'),
		createMapperGuideSourceField('countryCode', 'Country code', 'String'),
		createMapperGuideSourceField('status', 'Status', 'String', false, true, [
			{ value: 'ACTIVE', label: 'Active' },
			{ value: 'BLOCKED', label: 'Blocked' },
			{ value: 'PROSPECT', label: 'Prospect' }
		]),
		createMapperGuideSourceField('description', 'Description', 'String'),
		createMapperGuideSourceField('isActive', 'Is active', 'Boolean')
	];
	const sourceTreeItems = sourceFieldOptions.map(field => ({
		id: `guide-source-${field.path}`,
		displayName: field.label,
		path: field.path,
		dataType: field.dataType,
		canRead: true,
		canCreate: false,
		canUpdate: false,
		isMappable: true,
		isKey: field.isKey,
		isUniqueKey: field.isUniqueKey,
		isPicklist: field.isPicklist,
		canUseInLookup: field.canUseInLookup
	}));
	const targetSchema: MappingEntitySchema = {
		id: 'guide-target-account',
		systemName: targetSystemName,
		entityName: 'Account',
		canCreate: true,
		canUpdate: true,
		canQuery: true,
		supportsUdfs: false,
		primaryKeyPath: 'id',
		uniqueKeyPaths: ['accountCode'],
		fields: []
	};
	const targetTreeItems: TreeNode[] = [
		{ id: 'guide-target-customerNumber', displayName: 'Customer number', path: 'customerNumber', dataType: 'String', canRead: true, isMappable: true },
		{ id: 'guide-target-supplierNumber', displayName: 'Supplier number', path: 'supplierNumber', dataType: 'String', canRead: true, isMappable: true },
		{ id: 'guide-target-id', displayName: 'ID', path: 'id', dataType: 'String', canRead: true, isMappable: true }
	];
	const rows = buildMapperGuideRows(targetSystemName, sourceFieldOptions);

	return {
		sourceSystemName,
		targetSystemName,
		sourceFieldOptions,
		sourceTreeItems,
		targetSchema,
		targetTreeItems,
		rows,
		sections: buildMapperGuideSections(rows),
		caseDetails: buildMapperGuideCaseDetails(),
		markerLegend: buildMapperGuideMarkerLegend(),
		referenceFieldOptionsByEntity: {
			CustomerCategory: [
				createMapperGuideSourceField('ID', 'ID', 'String', true),
				createMapperGuideSourceField('Code', 'Code', 'String', true),
				createMapperGuideSourceField('Description', 'Description', 'String')
			]
		}
	};
}

export function createMapperGuideRowById(rowId: string, targetSystemName: string, sourceFieldOptions: FieldOption[]): MappingGridRow | null {
	return buildMapperGuideRows(targetSystemName, sourceFieldOptions).find(row => row.targetFieldId === rowId) ?? null;
}

export function createMapperGuideLookup(row: MappingGridRow): ManualLookupConfiguration {
	return {
		referenceEntityName: row.referenceEntityName || row.reference?.targetEntity || 'Customer category',
		returnFieldPath: row.reference?.defaultReturnFieldPath || 'ID',
		matchMode: 'All',
		criteria: [
			{
				targetFieldPath: 'Code',
				mode: 'SourceField',
				sourceFieldPath: 'classification',
				value: null
			}
		]
	};
}

export function getMapperGuideLookupReferenceFieldOptions(row: MappingGridRow | null, referenceFieldOptionsByEntity: Record<string, FieldOption[]>): FieldOption[] {
	if (!row) {
		return [];
	}

	const entityName = row.lookup?.referenceEntityName || row.referenceEntityName || row.reference?.targetEntity || '';
	const normalized = entityName.replace(/\s+/g, '').toLowerCase();
	return Object.entries(referenceFieldOptionsByEntity)
		.find(([key]) => key.replace(/\s+/g, '').toLowerCase() === normalized)?.[1] ?? [];
}

export function getMapperGuideRowState(row: MappingGridRow): 'issue' | 'ready' | 'ignored' | 'readonly' | 'pending' | 'loading' {
	if (row.readOnly) {
		return 'readonly';
	}

	if (row.mode === 'Ignore') {
		return row.required ? 'pending' : 'ignored';
	}

	return isMapperGuideRowUnmapped(row) ? 'pending' : 'ready';
}

export function getMapperGuideModeDescription(row: MappingGridRow): string {
	const detail = row.description ? `${row.description} ` : '';
	return `${detail}Changes here are demo-only and will not be saved.`;
}

export function isMapperGuideRowUnmapped(row: MappingGridRow): boolean {
	switch (row.mode) {
		case 'Ignore':
			return row.required;
		case 'SourceField':
			return !row.sourceFieldPath;
		case 'Constant':
		case 'Expression':
			return !row.value?.trim();
		case 'Lookup':
			return !row.lookup?.criteria?.length || !row.lookup.returnFieldPath;
		default:
			return true;
	}
}

export function getMapperGuideSampleExpression(row: MappingGridRow): string {
	if (row.targetPath === 'companyNumberWriteback') {
		return 'Iif([companyType] = 1, Result.customerNumber, Result.supplierNumber)';
	}

	if (/number|code/i.test(row.targetPath)) {
		return 'Iif(Form.companyType = 1, Form.companyNumber, null)';
	}

	return 'Iif(Form.companyType = 1, true, false)';
}

export function resolveMapperGuidePreviewValue(row: MappingGridRow): string {
	if (row.mode === 'Ignore') {
		return 'Not sent';
	}

	if (row.mode === 'Lookup') {
		return resolveMapperGuideLookupPreview(row);
	}

	if (row.mode === 'Constant') {
		return row.value ?? '';
	}

	if (row.mode === 'Expression') {
		return resolveMapperGuideExpressionPreview(row.value);
	}

	const mappedPicklistValue = resolveMapperGuidePicklistMapPreview(row);
	if (mappedPicklistValue !== null) {
		return mappedPicklistValue;
	}

	const sourceValue = getMapperGuideSourceValue(row.sourceFieldPath);
	return applyMapperGuideNormalization(sourceValue, row);
}

export function resolveMapperGuideExpressionPreview(expression: string | null | undefined): string {
	const value = expression ?? '';
	if (/Result\.customerNumber|Result\.supplierNumber/i.test(value)) {
		return '000042';
	}

	if (/companyNumber/i.test(value)) {
		return '42';
	}

	if (/companyType/i.test(value)) {
		return 'true';
	}

	return value || 'Expression result';
}

export function getMapperGuideSourceValue(path: string | null | undefined): string {
	switch (path) {
		case 'companyName':
			return ' Contoso Bikes ';
		case 'companyNumber':
			return '42';
		case 'companyType':
			return '1';
		case 'classification':
			return 'VIP';
		case 'countryCode':
			return 'NL';
		case 'status':
			return 'ACTIVE';
		case 'description':
			return 'Long customer description';
		case 'isActive':
			return 'false';
		default:
			return '';
	}
}

function buildMapperGuideRows(targetSystemName: string, sourceFieldOptions: FieldOption[]): MappingGridRow[] {
	const createRow = (options: Partial<MappingGridRow> & { id: string; path: string; displayName: string; mode: MappingRuleMode }): MappingGridRow => {
		const row: MappingGridRow = {
			targetFieldId: options.id,
			targetPath: options.path,
			targetDisplayName: options.displayName,
			targetDataType: options.targetDataType ?? 'String',
			required: options.required ?? false,
			requiredOnCreate: options.requiredOnCreate ?? options.required ?? false,
			requiredOnUpdate: options.requiredOnUpdate ?? false,
			canRead: options.canRead ?? true,
			canCreate: options.canCreate ?? true,
			canUpdate: options.canUpdate ?? true,
			readOnly: options.readOnly ?? false,
			isPicklist: options.isPicklist ?? false,
			isReference: options.isReference ?? false,
			isUserDefinedField: options.isUserDefinedField ?? false,
			isChildObject: options.isChildObject ?? false,
			isCollection: options.isCollection ?? false,
			isKey: options.isKey ?? false,
			isUniqueKey: options.isUniqueKey ?? false,
			canUseInLookup: options.canUseInLookup ?? true,
			description: options.description ?? null,
			format: options.format ?? null,
			maxLength: options.maxLength ?? null,
			minLength: options.minLength ?? null,
			referenceEntityName: options.referenceEntityName ?? null,
			reference: options.reference ?? null,
			allowedModes: options.allowedModes ?? ['Ignore', 'SourceField', 'Constant', 'Expression', 'Lookup'],
			targetPicklistValueMode: options.targetPicklistValueMode ?? null,
			targetPicklistOptions: options.targetPicklistOptions ?? [],
			mode: options.mode,
			isLinkKey: options.isLinkKey ?? false,
			sourceFieldPath: options.sourceFieldPath ?? null,
			sourceFieldOptions: options.sourceFieldOptions ?? sourceFieldOptions,
			value: options.value ?? null,
			lookup: options.lookup ?? null,
			picklistMapping: options.picklistMapping ?? null,
			normalization: options.normalization ?? null,
			previewValue: options.previewValue ?? null,
			isValid: options.isValid ?? true,
			validationMessage: options.validationMessage ?? null,
			childMappingCount: options.childMappingCount ?? 0,
			isResolving: false,
			resolvingMessage: null
		};

		row.previewValue ??= resolveMapperGuidePreviewValue(row);
		return row;
	};

	return [
		createRow({ id: 'guide-company', path: 'company', displayName: 'Company', description: 'Source field: choose one source path and the target gets that source value.', required: true, mode: 'SourceField', sourceFieldPath: 'companyName' }),
		createRow({ id: 'guide-ignore-optional', path: 'internalMemo', displayName: 'Optional memo', description: 'Ignore: leave an optional target field out of the payload.', mode: 'Ignore' }),
		createRow({ id: 'guide-payment-condition', path: 'paymentConditionCode', displayName: 'Payment condition', description: 'Text constant: type one fixed value and send it for every record.', mode: 'Constant', value: 'NET30' }),
		createRow({ id: 'guide-party-type', path: 'partyTypeEnum', displayName: 'Party type', description: 'Picklist constant: choose one allowed target option.', required: true, mode: 'Constant', value: 'ORGANIZATION', isPicklist: true, targetPicklistOptions: [{ value: 'ORGANIZATION', label: 'Organization' }, { value: 'PERSON', label: 'Person' }] }),
		createRow({ id: 'guide-status-map', path: 'status', displayName: 'Status mapping', description: 'Picklist map: copy a source picklist and map source values to target values.', mode: 'SourceField', sourceFieldPath: 'status', isPicklist: true, targetPicklistOptions: [{ value: 'A', label: 'Active', aliases: ['ACTIVE'] }, { value: 'B', label: 'Blocked', aliases: ['BLOCKED'] }, { value: 'P', label: 'Prospect', aliases: ['PROSPECT'] }], picklistMapping: { sourceFieldPath: 'status', items: [{ sourceValue: 'ACTIVE', sourceLabel: 'Active', targetValue: 'A' }, { sourceValue: 'BLOCKED', sourceLabel: 'Blocked', targetValue: 'B' }, { sourceValue: 'PROSPECT', sourceLabel: 'Prospect', targetValue: 'P' }] } }),
		createRow({ id: 'guide-customer', path: 'customer', displayName: 'Customer flag', description: 'Expression: build a value from source fields and logic.', mode: 'Expression', targetDataType: 'Boolean', value: 'Iif(Form.companyType = 1, true, false)' }),
		createRow({ id: 'guide-category', path: 'customerCategoryId', displayName: 'Customer category', description: 'Lookup: find a related target record and send its id/code.', mode: 'Lookup', isReference: true, referenceEntityName: 'Customer category', reference: { targetSystem: targetSystemName, targetEntity: 'CustomerCategory', defaultReturnFieldPath: 'ID' }, lookup: { referenceEntityName: 'Customer category', returnFieldPath: 'ID', matchMode: 'All', criteria: [{ targetFieldPath: 'Code', mode: 'SourceField', sourceFieldPath: 'classification', value: null }] } }),
		createRow({ id: 'guide-category-fixed', path: 'customerCategoryIdByDefault', displayName: 'Default category lookup', description: 'Lookup with fixed value: find a target reference by a constant code.', mode: 'Lookup', isReference: true, referenceEntityName: 'Customer category', reference: { targetSystem: targetSystemName, targetEntity: 'CustomerCategory', defaultReturnFieldPath: 'ID' }, lookup: { referenceEntityName: 'Customer category', returnFieldPath: 'ID', matchMode: 'All', criteria: [{ targetFieldPath: 'Code', mode: 'Constant', sourceFieldPath: null, value: 'DEFAULT' }] } }),
		createRow({ id: 'guide-account-code', path: 'accountCode', displayName: 'Account code', description: 'Link key: identify the target record before deciding create or update.', mode: 'SourceField', sourceFieldPath: 'companyNumber', isLinkKey: true, isKey: true, isUniqueKey: true, normalization: { padLeft: true, padLeftToLength: 6, padLeftChar: '0' } }),
		createRow({ id: 'guide-trim-name', path: 'trimmedCompanyName', displayName: 'Trim company name', description: 'Normalization: trim leading and trailing spaces before sending a source field.', mode: 'SourceField', sourceFieldPath: 'companyName', normalization: { trimWhitespace: true } }),
		createRow({ id: 'guide-truncate-description', path: 'shortDescription', displayName: 'Short description', description: 'Normalization: truncate long text to the target maximum length.', mode: 'SourceField', sourceFieldPath: 'description', maxLength: 12, normalization: { truncateToMaxLength: true, maxLength: 12, truncateSuffix: '...' } }),
		createRow({ id: 'guide-country', path: 'addressCountryCode', displayName: 'Country on create', description: 'Ignore: do not send a value. Useful on update when the target should keep its current value.', mode: 'Ignore', sourceFieldPath: 'countryCode' }),
		createRow({ id: 'guide-writeback', path: 'companyNumberWriteback', displayName: 'Writeback value', description: 'Writeback uses the returned target value after a successful run; this row shows the expression shape.', mode: 'Expression', value: 'Iif([companyType] = 1, Result.customerNumber, Result.supplierNumber)', canCreate: false, canUpdate: false })
	];
}

function createMapperGuideSourceField(path: string, label: string, dataType: string, isKey = false, isPicklist = false, picklistOptions: FieldOption['picklistOptions'] = []): FieldOption {
	return {
		id: `guide-source-${path}`,
		path,
		label,
		dataType,
		isUserDefinedField: false,
		canRead: true,
		canCreate: false,
		canUpdate: false,
		isKey,
		isUniqueKey: isKey,
		canUseInLookup: true,
		isPicklist,
		picklistOptions
	};
}

function buildMapperGuideSections(rows: MappingGridRow[]): MapperGuideSection[] {
	const getRows = (...ids: string[]) => ids.map(id => rows.find(row => row.targetFieldId === id)).filter((row): row is MappingGridRow => !!row);
	return [
		{ title: 'Basic values', description: 'Copy, skip, or send fixed values.', rows: getRows('guide-company', 'guide-ignore-optional', 'guide-payment-condition', 'guide-party-type') },
		{ title: 'Logic and references', description: 'Use expressions, lookups, and picklist mappings.', rows: getRows('guide-customer', 'guide-category', 'guide-category-fixed', 'guide-status-map') },
		{ title: 'Matching and cleanup', description: 'Match records and normalize values before sending.', rows: getRows('guide-account-code', 'guide-trim-name', 'guide-truncate-description') },
		{ title: 'Run behavior', description: 'Handle create/update safety and writeback values.', rows: getRows('guide-country', 'guide-writeback') }
	];
}

function buildMapperGuideCaseDetails(): Record<string, MapperGuideCaseDetail> {
	return {
		'guide-company': { tabs: ['Create', 'Update'], title: 'Copy one source value into one target field', summary: 'Use Source field when the target should receive exactly one value from the source record. This is the most common mapping mode.', steps: ['Choose SourceField in the Mode column.', 'Pick the source path in the Mapping column.', 'Click Preview to see the target value that will be sent.'], exampleInput: 'companyName = " Contoso Bikes "', exampleResult: 'company = " Contoso Bikes "' },
		'guide-ignore-optional': { tabs: ['Create', 'Update'], title: 'Leave a target field out of the payload', summary: 'Use Ignore when a target field should not be sent. This is safe for optional fields and useful when the target already manages the value.', steps: ['Choose Ignore in the Mode column.', 'Check that the Status column says ignored.', 'Use this only when the target field is not required for that tab.'], exampleInput: 'Source has internal notes.', exampleResult: 'internalMemo is not sent to the target.' },
		'guide-payment-condition': { tabs: ['Create', 'Update'], title: 'Send the same text for every record', summary: 'Use Constant when all records should get the same target value, such as a default payment condition or fixed type code.', steps: ['Choose Constant in the Mode column.', 'Type the exact value expected by the target.', 'Preview to confirm the fixed value is sent.'], exampleInput: 'Any source account.', exampleResult: 'paymentConditionCode = NET30' },
		'guide-party-type': { tabs: ['Create', 'Update'], title: 'Choose one fixed target picklist option', summary: 'Picklist constants work like normal constants, but the grid limits the value to target-supported options.', steps: ['Choose Constant in the Mode column.', 'Select an option from the target picklist.', 'Use this when every record should have the same target option.'], exampleInput: 'Any company record.', exampleResult: 'partyTypeEnum = ORGANIZATION' },
		'guide-status-map': { tabs: ['Create', 'Update'], title: 'Map source picklist values to target picklist values', summary: 'Use Source field plus Map when both systems use picklists but the values are not identical.', steps: ['Choose SourceField and pick a source picklist field.', 'Click Map in the Mapping column.', 'Match each source option to the correct target option and Apply.'], exampleInput: 'status = ACTIVE', exampleResult: 'status = A after picklist mapping' },
		'guide-customer': { tabs: ['Create', 'Update'], title: 'Calculate a value with an expression', summary: 'Use Expression when the target value depends on conditions, multiple fields, or functions.', steps: ['Choose Expression in the Mode column.', 'Click Edit and build the expression with source fields.', 'Use Test expression before applying it to the row.'], exampleInput: 'companyType = 1', exampleResult: 'customer = true' },
		'guide-category': { tabs: ['Create', 'Update'], title: 'Resolve a target reference from a source field', summary: 'Use Lookup when the target expects a reference id/code instead of the source value itself.', steps: ['Choose Lookup in the Mode column.', 'Click Edit lookup.', 'Choose the target match field and bind it to a source field.'], exampleInput: 'classification = VIP', exampleResult: 'customerCategoryId = ID for Code = VIP' },
		'guide-category-fixed': { tabs: ['Create', 'Update'], title: 'Resolve a target reference from a fixed value', summary: 'A lookup criterion can also use a constant. This is useful when all records should point to the same target reference.', steps: ['Choose Lookup in the Mode column.', 'Use Constant inside the lookup criterion.', 'Type the target code that should be matched.'], exampleInput: 'Fixed lookup code = DEFAULT', exampleResult: 'customerCategoryId = ID for Code = DEFAULT' },
		'guide-account-code': { tabs: ['Create', 'Update'], title: 'Use a mapped value as the record link key', summary: 'The link key identifies the target record. If a match is found, the mapper updates; otherwise it creates.', steps: ['Map the row with SourceField or Expression.', 'Tick the Key checkbox.', 'Preview and confirm the matched value is stable and unique.'], exampleInput: 'companyNumber = 42', exampleResult: 'accountCode = 000042 and is used to find the target record' },
		'guide-trim-name': { tabs: ['Create', 'Update'], title: 'Trim spaces before sending a value', summary: 'Normalization changes the mapped value after mapping but before it is posted to the target.', steps: ['Map the source field.', 'Open the normalization row below the mapping.', 'Enable Trim and preview the cleaned value.'], exampleInput: 'companyName = " Contoso Bikes "', exampleResult: 'trimmedCompanyName = "Contoso Bikes"' },
		'guide-truncate-description': { tabs: ['Create', 'Update'], title: 'Shorten text to fit target limits', summary: 'Use truncate normalization when the target has a smaller maximum length than the source value.', steps: ['Map the source field.', 'Enable Truncate in normalization.', 'Set the maximum length and optional suffix.'], exampleInput: 'description = Long customer description', exampleResult: 'shortDescription = Long cust...' },
		'guide-country': { tabs: ['Update'], title: 'Protect fields on Update', summary: 'Use different Create and Update mappings when a field should only be sent for new records. In the real mapper, switch between the Create and Update headers to configure this.', steps: ['Open the Update header in the real mapper.', 'Set the sensitive field to Ignore.', 'Keep the field mapped on Create if new records still need it.'], exampleInput: 'countryCode = NL on an existing target account', exampleResult: 'Update sends no country value, so the target keeps its current country.' },
		'guide-writeback': { tabs: ['Writeback'], title: 'Write target results back to the source after success', summary: 'Writeback is configured under the Writeback header, not in the Create or Update mapping grid. It runs after a successful target create/update.', steps: ['Open the Writeback header in the real mapper.', 'Choose the source field to update.', 'Use a returned target field or expression with Result.field values.'], exampleInput: 'Result.customerNumber = 000042', exampleResult: 'source companyNumber is updated to 000042 after the run succeeds.' }
	};
}

function buildMapperGuideMarkerLegend(): MapperGuideMarkerLegendItem[] {
	return [
		{ className: 'required', label: 'Required star', description: 'This target field is required for the selected Create or Update tab.' },
		{ className: 'link-key', label: 'Record link key', description: 'This mapped value is used to find the existing target record before create/update.' },
		{ className: 'reference', label: 'Reference chain', description: 'The target field points to another target record, usually configured with Lookup.' },
		{ className: 'picklist', label: 'Picklist', description: 'The target accepts a limited list of values; use Constant or Map when values differ.' },
		{ className: 'readonly', label: 'Read-only lock', description: 'The target field can be read but cannot be mapped or sent.' }
	];
}

function resolveMapperGuideLookupPreview(row: MappingGridRow): string {
	const criterion = row.lookup?.criteria?.[0];
	if (!criterion?.targetFieldPath) {
		return 'Choose a lookup match field';
	}

	const matchValue = criterion.mode === 'SourceField'
		? getMapperGuideSourceValue(criterion.sourceFieldPath)
		: criterion.value || '';

	return matchValue
		? `${row.lookup?.returnFieldPath || 'ID'} for ${criterion.targetFieldPath} = ${matchValue}`
		: 'Choose a lookup match value';
}

function resolveMapperGuidePicklistMapPreview(row: MappingGridRow): string | null {
	if (!row.isPicklist || row.mode !== 'SourceField' || !row.picklistMapping) {
		return null;
	}

	const sourceValue = getMapperGuideSourceValue(row.sourceFieldPath);
	const mapping = row.picklistMapping.items.find(item => item.sourceValue === sourceValue);
	return mapping?.targetValue || `Unmapped source value: ${sourceValue}`;
}

function applyMapperGuideNormalization(value: string, row: MappingGridRow): string {
	const normalization = row.normalization;
	let normalized = value;

	if (normalization?.trimWhitespace) {
		normalized = normalized.trim();
	}

	if (normalization?.emptyStringToNull && normalized === '') {
		return 'null';
	}

	if (normalization?.truncateToMaxLength && normalization.maxLength && normalized.length > normalization.maxLength) {
		const suffix = normalization.truncateSuffix ?? '';
		normalized = `${normalized.slice(0, Math.max(0, normalization.maxLength - suffix.length))}${suffix}`;
	}

	if (normalization?.padLeft && normalization.padLeftToLength) {
		normalized = normalized.padStart(normalization.padLeftToLength, normalization.padLeftChar || ' ');
	}

	return normalized;
}
